import { NodeType, NodeCheckFunType } from './types';

export const NODE_KEYS = {
  NODE_LEVEL_KEY: '__virtualized-tree-node-level__',
  NODE_LOADER_KEY: '__virtualized-tree-node-loader__',
  NODE_ERROR_KEY: '__virtualized-tree-node-error__',
  NODE_SEPARATOR_KEY: '__virtualized-tree-node-separator__',
  NODE_ACTION_TYPE: '__virtualized-tree-action-type__'
} as const;

export const hasChildren = (children: NodeType[] | undefined) => Array.isArray(children) && children.length > 0;
export const cloneArray = (array: any) => {
  return !Array.isArray(array) ? [] : [...array];
};

const keyMap = new Map();
const saveKeyCount = (node: NodeType, valueKey: any) => {
  if (valueKey === NODE_KEYS.NODE_ACTION_TYPE) return;

  const key = node[valueKey];
  keyMap.set(key, (keyMap.get(key) || 0) + 1);
};

const reduceNodes = ({
  nodes,
  valueKey,
  childrenKey,
  expandedKeys,
  flatNodes = [],
  level = 0,
  innerState,
  innerPath = ''
}: {
  nodes: NodeType[];
  valueKey: string;
  childrenKey: string;
  expandedKeys: NodeType[];
  flatNodes?: NodeType[];
  level?: number;
  innerState: Record<string, any>;
  innerPath?: string;
}) => {
  return nodes.reduce((preValue, node) => {
    const curPath = `${innerPath}/${node[valueKey]}`;
    const newNode = {
      ...node,
      innerPath: curPath,
      [NODE_KEYS.NODE_LEVEL_KEY]: level
    };
    preValue.push(newNode);
    saveKeyCount(newNode, valueKey);

    if (expandedKeys.includes(newNode[valueKey])) {
      const innerNode = innerState[node[valueKey]];

      if (innerNode) {
        preValue.push({
          ...innerNode,
          innerPath: `${curPath}/${innerNode[valueKey]}`,
          [NODE_KEYS.NODE_LEVEL_KEY]: level + 1
        });
        saveKeyCount(innerNode, valueKey);
      } else {
        preValue = reduceNodes({
          nodes: newNode[childrenKey] || [],
          valueKey,
          childrenKey,
          expandedKeys,
          flatNodes: preValue,
          level: level + 1,
          innerState,
          innerPath: curPath
        });
      }
    }

    return preValue;
  }, flatNodes);
};

export const genFlatNodes = ({
  nodes,
  valueKey,
  childrenKey,
  expandedKeys,
  innerState
}: {
  nodes: NodeType[];
  valueKey: string;
  childrenKey: string;
  expandedKeys: KeyType[];
  innerState: Record<string, any>;
}) => {
  keyMap.clear();
  return {
    flatNodes: reduceNodes({ nodes, valueKey, childrenKey, expandedKeys, innerState }),
    duplicatedKeys: [...keyMap.keys()].filter((key) => keyMap.get(key) > 1)
  };
};

export const isDisabled = (node: NodeType, isNodeDisabled: NodeCheckFunType) => node.isDisabled || isNodeDisabled(node);
export const isActionNode = (node: NodeType) => !!node[NODE_KEYS.NODE_ACTION_TYPE];

export const getSelectableKeys = ({
  flatNodes,
  valueKey,
  preNodeKey,
  curNodeKey,
  isNodeDisabled,
  isNodeSelectable,
  allowCrossParent
}: {
  flatNodes: NodeType[];
  valueKey: string;
  preNodeKey: KeyType;
  curNodeKey: KeyType;
  isNodeDisabled: NodeCheckFunType;
  isNodeSelectable: NodeCheckFunType;
  allowCrossParent: boolean;
}) => {
  const preNode = flatNodes.find((node) => node[valueKey] === preNodeKey);
  const curNode = flatNodes.find((node) => node[valueKey] === curNodeKey);
  if (!preNode || !curNode) return [curNodeKey];

  const level = curNode[NODE_KEYS.NODE_LEVEL_KEY];
  if (preNode[NODE_KEYS.NODE_LEVEL_KEY] !== level) return [curNodeKey];

  const [start, end] = [flatNodes.indexOf(preNode), flatNodes.indexOf(curNode)].sort((a, b) => a - b);
  const nodesInRange = flatNodes.slice(start, end + 1);

  // To avoid select nodes under different parent node.
  if (!allowCrossParent && nodesInRange.some((node) => node[NODE_KEYS.NODE_LEVEL_KEY] < level)) return [curNodeKey];
  return nodesInRange
    .filter((node) => {
      if (isActionNode(node) || isDisabled(node, isNodeDisabled) || !isNodeSelectable(node)) return false;
      if (node.isEditing || node[NODE_KEYS.NODE_LEVEL_KEY] !== level) return false;
      return true;
    })
    .map((node) => node[valueKey]);
};
